/*----------------------------------
- DOMAINS RAIL MODEL
----------------------------------*/

/**
 * What the public filter rail draws and what each of its clicks names, kept out of the components so the order, the
 * lock and the intended rule have one home.
 *
 * The order and the lock are Browse's own (`sortRadarRuleDefinitions`, `isAdvancedRadarFilterId`), read the way the
 * shipped `RadarRuleControls` read them: active rules first, locked filters last. The rules a click builds are the
 * ones Browse's controls build (`upsertConditionRule`, `IN` for a choice, the pattern's own operator), so the query a
 * visitor carries into authentication is the query Browse would have applied.
 *
 * @docs docs/features/website-domains-hubs
 * @adr  ADR-0035
 * @rule Nothing here applies a rule: every builder returns the query a wall carries in `returnTo`.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { isAdvancedRadarFilterId } from '@uniquedomains/components/radar/catalogs/filterTiers';
import { sortRadarRuleDefinitions } from '@uniquedomains/components/radar/catalogs/radarRuleControls';
import { formatChipValue, removeConditionRule, upsertConditionRule } from '@uniquedomains/components/radar/rules/engine';
import type {
    RadarConditionOp,
    RadarFilterDef,
    RadarQuery,
    RadarRuleCondition,
} from '@uniquedomains/components/radar/rules/types';

import { DOMAINS_RAIL_COPY } from '@/client/catalogs/landing/domains/pageCopy';

/*----------------------------------
- TYPES
----------------------------------*/

/** One filter as the rail draws it. */
export type RailFilter = {
    filter: RadarFilterDef;
    rule: RadarRuleCondition | null;
    /** Advanced for a visitor: the head is the plan's wall and the filter never opens (ADR-0083). */
    locked: boolean;
    /** "Any", or the rule's own summary on a search that carries one. */
    summary: string;
};

/** The pattern's two directions, as Browse's text control offers them. */
export type PatternOperator = Extract<RadarConditionOp, 'STARTS_WITH' | 'ENDS_WITH'>;

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The histogram's bar count: text-free, so no share is claimed on a surface that has none (mock `railHtml`). */
export const RANGE_HISTOGRAM_BARS = 10;

const rangeNumber = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/*----------------------------------
- HELPERS
----------------------------------*/

const ruleValues = (rule: RadarRuleCondition | null): string[] => {
    if (!rule) return [];
    return (Array.isArray(rule.value) ? rule.value : [rule.value]).map(String);
};

const buildRule = (filter: RadarFilterDef, op: RadarConditionOp, value: RadarRuleCondition['value']): RadarRuleCondition => ({
    filterId: filter.id,
    field: filter.field ?? filter.id,
    op,
    value,
});

const withRule = (query: RadarQuery, filter: RadarFilterDef, rule: RadarRuleCondition | null): RadarQuery => ({
    ...query,
    rules: rule ? upsertConditionRule(query.rules, rule) : removeConditionRule(query.rules, filter.id),
});

/*----------------------------------
- PUBLIC API
----------------------------------*/

/**
 * The filters in the rail's order. The keyword editor is left out as on Browse's rail (`showKeywordEditor` false):
 * the keyword is the prompt, not a filter. A rule whose filter is not in the catalog has no control here (Browse drew
 * it as a removable legacy row); no hub or sector page pre-applies one today (mock head comment: "The rail is the same
 * on every list").
 */
export const resolveRailFilters = (filters: readonly RadarFilterDef[], query: RadarQuery): RailFilter[] => {
    const activeIds = new Set(query.rules.map((rule) => rule.filterId));
    const visible = filters.filter((filter) => filter.status === 'active' && filter.mode.includes(query.mode) && filter.id !== 'keyword');
    const ordered = sortRadarRuleDefinitions(visible, query.mode).sort((left, right) => {
        const activeOrder = Number(activeIds.has(right.id)) - Number(activeIds.has(left.id));
        if (activeOrder !== 0) return activeOrder;
        return Number(isAdvancedRadarFilterId(left.id)) - Number(isAdvancedRadarFilterId(right.id));
    });

    return ordered.map((filter) => {
        const rule = query.rules.find((candidate) => candidate.filterId === filter.id) ?? null;
        return {
            filter,
            rule,
            locked: isAdvancedRadarFilterId(filter.id),
            summary: rule ? formatChipValue(rule, filter) || DOMAINS_RAIL_COPY.active : DOMAINS_RAIL_COPY.any,
        };
    });
};

/** The values a choice filter's rule already holds, to check their options. */
export const readSelectedValues = (rule: RadarRuleCondition | null): ReadonlySet<string> => new Set(ruleValues(rule));

/**
 * The query a click on one option names: the search on screen plus that option toggled, as Browse's choice control
 * commits it (`IN`; a single-choice filter replaces its value).
 */
export const buildChoiceIntent = (query: RadarQuery, entry: RailFilter, value: string): RadarQuery => {
    const selected = entry.filter.control === 'select' ? [] : ruleValues(entry.rule);
    const next = selected.includes(value) ? selected.filter((candidate) => candidate !== value) : [...selected, value];
    return withRule(query, entry.filter, next.length > 0 ? buildRule(entry.filter, 'IN', next) : null);
};

/** The query Apply names: the search on screen plus the pattern, in the direction the visitor picked. */
export const buildPatternIntent = (query: RadarQuery, entry: RailFilter, operator: PatternOperator, pattern: string): RadarQuery =>
    withRule(query, entry.filter, pattern ? buildRule(entry.filter, operator, pattern) : null);

/** The pattern's direction on arrival: the rule's own, else the catalog's default, else Starts with. */
export const readPatternOperator = (entry: RailFilter): PatternOperator => {
    const op = entry.rule?.op ?? entry.filter.defaultOperator;
    return op === 'ENDS_WITH' ? 'ENDS_WITH' : 'STARTS_WITH';
};

/** A range's two bounds, from the catalog (Browse's `getBounds`). */
export const readRangeBounds = (filter: RadarFilterDef): [string, string] => {
    const defaults = Array.isArray(filter.defaultValue) && filter.defaultValue.length === 2 ? filter.defaultValue : null;
    const min = filter.rangeMin ?? (defaults && typeof defaults[0] === 'number' ? defaults[0] : 0);
    const max = filter.rangeMax ?? (defaults && typeof defaults[1] === 'number' ? defaults[1] : 100);
    return [rangeNumber.format(min), rangeNumber.format(max)];
};
